import { useEffect, useRef, useState } from 'react';

const categories = [
  {
    label: 'Création de site web',
    projects: [
      {
        title: 'Melio-LM',
        type: 'SITE WEB',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/a27120ea4b52453bb7b095743bfe37ab.png',
        link: 'https://www.melio-lm.ch/',
      },
      {
        title: 'Lalou Maestra',
        type: 'SITE WEB',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/1826fbcc1dc255972835c58ce56f5b60.png',
        link: 'https://laloumaestra.ch/',
      },
      {
        title: 'Plug and Bar',
        type: 'SITE WEB',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/33192dd2e759e7858901ef724c639224.png',
        link: 'https://readdy.cc/preview/d8db5950-dd7c-40d9-b2de-72ddcdf21388/9255217/',
      },
    ],
  },
  {
    label: 'Supports de communication',
    projects: [
      {
        title: 'Carte de fidélité',
        type: 'PRINT',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/bf9ec2f20cb27d0bf82873dadd9490ce.jpeg',
      },
      {
        title: 'Carte de visite',
        type: 'PRINT',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/35c5478846ed8a2e6b7ca77da100a1f9.png',
      },
    ],
  },
  {
    label: 'Gestion des réseaux sociaux',
    projects: [
      {
        title: 'Publication',
        type: 'RÉSEAUX SOCIAUX',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/5afe1b8b3009f96f3026025fc2832224.jpeg',
      },
      {
        title: 'Story\u00a0',
        type: 'RÉSEAUX SOCIAUX',
        image: 'https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/a3d02083fbf208cc6b0e462972db2d17.png',
      },
    ],
  },
];

export default function Realisations() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="realisations"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className={`mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand mb-4 block">
            réalisations
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium text-white mb-4">
            Mes réalisations
          </h2>
          <p className="text-white/50 max-w-xl text-base md:text-lg font-light">
            Des projets uniques, conçus avec soin et attention aux détails
          </p>
        </div>

        {/* Categories */}
        <div className="space-y-20">
          {categories.map((category, catIndex) => (
            <div
              key={category.label}
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${0.15 + catIndex * 0.15}s` }}
            >
              {/* Category Label */}
              <div className="flex items-center gap-4 mb-8">
                <h3 className="text-sm md:text-base font-display font-medium text-white/80 tracking-wide">
                  {category.label}
                </h3>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {category.projects.map((project, projIndex) => {
                  const cardContent = (
                    <>
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h4 className="text-lg md:text-xl font-display font-medium text-white">
                          {project.title}
                        </h4>
                      </div>

                      {/* Hover Arrow */}
                      <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                        <i className="ri-arrow-right-up-line text-white text-sm" />
                      </div>
                    </>
                  );

                  return (
                    <div key={project.title} className="flex flex-col gap-4">
                      {project.link ? (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative block overflow-hidden rounded-xl bg-dark-card transition-all duration-500 hover:scale-[1.01]"
                          style={{
                            transitionDelay: `${0.2 + catIndex * 0.15 + projIndex * 0.08}s`,
                          }}
                        >
                          {cardContent}
                        </a>
                      ) : (
                        <div
                          className="group relative overflow-hidden rounded-xl bg-dark-card transition-all duration-500 hover:scale-[1.01]"
                          style={{
                            transitionDelay: `${0.2 + catIndex * 0.15 + projIndex * 0.08}s`,
                          }}
                        >
                          {cardContent}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}